<script setup lang="ts">
import { computed, toRef } from "vue";
import { ArrowDownRight } from "lucide-vue-next";
import { useAnalysisQuery } from "@/composables/useAnalysisQuery";
import { resolveLocalizedTitle } from "@/composables/useCardData";
import { removeHtmlMarkdownAndLineBreak } from "@utils/toolBox";
import { Tooltip } from "@/components/ui/tooltip";
import type { ChartCard, KeyDriversData } from "@/types";
import { matrixScale, useAnalysisContext, useCardPopulation } from "../analysisData";
import AnalysisCardShell from "./AnalysisCardShell.vue";
import PopulationSwitch from "./PopulationSwitch.vue";

const props = defineProps({
  card: { type: Object, required: true },
  readonly: { type: Boolean, default: false },
});

const card = toRef(props, "card");
const { weighted, availableCards, displayLocale } = useAnalysisContext();
const { choices, selected, filters, color: populationColor } = useCardPopulation(card);

const globalQuestionId = computed<number | null>(() => props.card.content?.global_question_id || null);
const matrixQuestionIds = computed<number[]>(() => props.card.content?.matrix_question_ids || []);

const body = computed(() => {
  if (!globalQuestionId.value || matrixQuestionIds.value.length < 2) return null;
  return { global_question_id: globalQuestionId.value, driver_question_ids: matrixQuestionIds.value, filters: filters.value };
});

const { result, loading, state } = useAnalysisQuery<KeyDriversData>("key_drivers", body);

const cards = computed(() => availableCards.value as ChartCard[]);
const globalMeta = computed(() => cards.value.find((c) => (c.questions || []).some((q) => q.id === globalQuestionId.value)) || null);
const matrixMeta = computed(
  () => cards.value.find((c) => c.type === "q_matrix" && (c.questions || []).some((q) => matrixQuestionIds.value.includes(q.id))) || null,
);
const scale = computed(() => matrixScale(matrixMeta.value?.specs));

const respondents = computed(() => {
  if (!result.value) return null;
  return weighted.value ? result.value.weighted_respondents : result.value.respondents;
});
const r2 = computed(() => (weighted.value ? result.value?.weighted_r2 : result.value?.r2) ?? null);
const status = computed(() => result.value?.status || null);

const resolveTitle = (title: unknown) => removeHtmlMarkdownAndLineBreak(resolveLocalizedTitle(title, displayLocale.value));

function driverLabel(questionId: number) {
  const question = (matrixMeta.value?.questions || []).find((q) => q.id === questionId);
  return resolveTitle(question?.item_title) || question?.name || `Q${questionId}`;
}

const accent = computed(() => props.card.options?.color || populationColor.value);

const rows = computed(() => {
  const drivers = (result.value?.drivers || []).filter((driver) => !driver.left_out).map((driver) => ({
    questionId: driver.question_id,
    label: driverLabel(driver.question_id),
    weight: weighted.value ? driver.weighted_relative_weight : driver.relative_weight,
    correlation: weighted.value ? driver.weighted_correlation : driver.correlation,
    mean: weighted.value ? driver.weighted_mean : driver.mean,
  }));
  const heaviest = Math.max(...drivers.map((driver) => driver.weight ?? 0), 0.001);
  const range = Math.max(scale.value.max - scale.value.min, 1);
  return drivers
    .sort((a, b) => (b.weight ?? -1) - (a.weight ?? -1))
    .map((driver) => ({
      ...driver,
      negative: (driver.correlation ?? 0) < 0,
      width: driver.weight == null ? 0 : (driver.weight / heaviest) * 100,
      performance: driver.mean == null ? 0 : Math.min(100, Math.max(0, ((driver.mean - scale.value.min) / range) * 100)),
    }));
});

const leftOut = computed(() => (result.value?.drivers || []).filter((driver) => driver.left_out).map((driver) => driverLabel(driver.question_id)));

const cardState = computed(() => state(result.value !== null));

const formatWeight = (value: number | null) => (value == null ? "–" : `${Math.round(value)}%`);
const formatMean = (value: number | null) => (value == null ? "–" : value.toFixed(1));
</script>

<template>
  <AnalysisCardShell
    :card="card"
    :readonly="readonly"
    :respondents="respondents"
    :weighted="weighted"
    respondents-hint="Respondents who answered the score and every aspect, the base of the model."
    description="Which aspects of a matrix move a global score. The explained part of the score (R²) is split across the aspects as relative weights that add up to 100%, so they still rank the aspects when a halo squeezes every correlation together. Each weight comes with the aspect's average score: a heavy aspect already rated high is not where to start."
    :state="cardState"
    state-message="Select a score and the matrix of its aspects to find what moves the score.">
    <template #subtitle>
      <p v-if="globalMeta || matrixMeta" class="text-xs text-muted-foreground">
        {{ globalMeta?.name || "Score" }} explained by {{ matrixMeta?.name || "aspects" }}
        <template v-if="r2 != null">
          ·
          <Tooltip
            content="Share of the score's variation the aspects explain together. Below 0.3, most of what moves the score is outside this matrix and the ranking says little."
            :delay-duration="300">
            <span class="tabular-nums">R² {{ r2.toFixed(2) }}</span>
          </Tooltip>
        </template>
      </p>
    </template>
    <template #toolbar>
      <PopulationSwitch v-if="choices.length > 1" :card="card" :populations="choices" :selected="selected" />
    </template>

    <div
      v-if="status === 'insufficient'"
      class="rounded-md border border-status-warning-border bg-status-warning-soft px-3 py-2 text-xs leading-snug text-status-warning-strong">
      <span class="font-semibold">Base too small.</span>
      {{ result?.respondents }} respondents answered the score and every aspect; the weights need at least
      {{ result?.required_respondents }}, ten per aspect.
    </div>
    <div
      v-else-if="status === 'unstable'"
      class="rounded-md border border-status-warning-border bg-status-warning-soft px-3 py-2 text-xs leading-snug text-status-warning-strong">
      <span class="font-semibold">No model to fit.</span>
      The score and at least two aspects must vary across respondents for the weights to mean anything.
    </div>
    <div v-else class="flex flex-col gap-1.5" :class="{ 'opacity-50': loading }">
      <div class="grid grid-cols-[160px_minmax(0,1fr)_7rem] items-center gap-x-2 heading-label text-foreground-subtle">
        <span />
        <span>Relative weight</span>
        <span>Score · {{ scale.min }}–{{ scale.max }}</span>
      </div>
      <div
        v-for="row in rows"
        :key="row.questionId"
        class="grid grid-cols-[160px_minmax(0,1fr)_7rem] items-center gap-x-2">
        <Tooltip trigger-class="block min-w-0 !cursor-default">
          <span class="block text-xs leading-[15px] font-medium line-clamp-2 break-words text-right">{{ row.label }}</span>
          <template #content>{{ row.label }}</template>
        </Tooltip>
        <Tooltip :delay-duration="150" content-class="max-w-xs">
          <div class="flex items-center gap-2">
            <div class="relative h-5 flex-1 overflow-hidden rounded bg-muted">
              <div class="h-full rounded transition-all duration-300" :style="{ width: `${row.width}%`, backgroundColor: accent }" />
            </div>
            <span class="flex w-12 shrink-0 items-center justify-end text-xs font-semibold tabular-nums">
              {{ formatWeight(row.weight) }}
              <ArrowDownRight v-if="row.negative" class="ml-0.5 h-3 w-3 text-status-error" />
            </span>
          </div>
          <template #content>
            <div class="font-medium">{{ row.label }}</div>
            <div class="tabular-nums">{{ formatWeight(row.weight) }} of the explained score</div>
            <div class="tabular-nums">Average {{ formatMean(row.mean) }} on {{ scale.min }}–{{ scale.max }}</div>
            <div v-if="row.correlation != null" class="tabular-nums">Correlation with the score {{ row.correlation.toFixed(2) }}</div>
            <div v-if="row.negative" class="mt-1 border-t border-white/20 pt-1">Linked negatively: the higher this aspect is rated, the lower the score.</div>
            <div v-else-if="row.weight == null" class="mt-1 border-t border-white/20 pt-1">Rated the same by every respondent, so it cannot explain the score.</div>
          </template>
        </Tooltip>
        <div class="flex items-center gap-2">
          <div class="relative h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
            <div class="h-full rounded-full bg-border-strong transition-all duration-300" :style="{ width: `${row.performance}%` }" />
          </div>
          <span class="w-8 shrink-0 text-right text-xs tabular-nums">{{ formatMean(row.mean) }}</span>
        </div>
      </div>
    </div>
    <p v-if="leftOut.length" class="text-2xs text-muted-foreground">
      Left out, answered by fewer than half of the respondents: {{ leftOut.join(", ") }}.
    </p>
  </AnalysisCardShell>
</template>
